import React from "react";
import { formatFoundingRemaining } from "@/lib/billingClient";

/**
 * Founding 500 live counter — presentational only. The parent owns the
 * single polling hook (useFounding500) and passes state down, so there is
 * exactly one timer per surface. Values come ONLY from the backend
 * (Paddle-authoritative); nothing is estimated or decremented client-side.
 *
 * States:
 *  - loading (no data yet)     → "Checking Founding 500 availability..."
 *  - error   (no data yet)     → "Founding 500 availability is being confirmed."
 *  - inactive / exhausted      → renders nothing (parent shows standard pricing)
 *  - active                    → "N FOUNDING PLACES REMAINING" + thin progress bar
 */
export const Founding500Counter = ({ loading, error, data, compact = false }) => {
  if (!data) {
    if (loading) {
      return (
        <p aria-live="polite" data-testid="f500-loading"
          className="text-[11px] text-neutral-500 text-center">
          Checking Founding 500 availability...
        </p>
      );
    }
    if (error) {
      return (
        <p aria-live="polite" data-testid="f500-confirming"
          className="text-[11px] text-neutral-500 text-center">
          Founding 500 availability is being confirmed.
        </p>
      );
    }
    return null;
  }
  if (!data.offer_active || data.remaining <= 0) return null;

  const limit = data.limit || 500;
  const pct = Math.round(Math.min(100, Math.max(0, (data.claimed / limit) * 100)) * 10) / 10;

  return (
    <div aria-live="polite" data-testid="f500-counter" className={compact ? "text-center" : "text-center mx-auto max-w-xs"}>
      <div
        data-testid="f500-remaining-text"
        className={
          compact
            ? "inline-block rounded-full border border-amber-400/40 bg-neutral-900 px-3 py-1 text-[11px] font-bold tracking-wide text-amber-300"
            : "inline-block rounded-full border border-amber-400/50 bg-neutral-900 px-4 py-1.5 text-sm font-bold tracking-wide text-amber-300"
        }
      >
        {formatFoundingRemaining(data.remaining)}
      </div>
      {!compact && (
        <div className="mt-2 h-1 w-full rounded-full bg-neutral-800 overflow-hidden" data-testid="f500-progress" role="presentation">
          <div
            className="h-full rounded-full bg-amber-400 transition-[width] duration-700"
            style={{ width: `${pct}%` }}
            data-testid="f500-progress-fill"
          />
        </div>
      )}
    </div>
  );
};
